import React from "react";
import { FormattedNumber } from "react-intl";
import { gql, useQuery } from "@apollo/client";

import {
  Container,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import Layout from "components/common/Layout";
import StatsCard from "components/stats/StatsCard";
import StatsChart from "components/stats/StatsChart";

const makeQuery = (statistic) => gql`
  query (
    $step: StatisticsArg_Step
    $range: StatisticsArg_DateRange
    $isB2B: Boolean!
  ) {
    statistics {
      stat: ${statistic}(step: $step, range: $range, isB2B: $isB2B) {
        ...StatsChart_statistic
      }
    }
  }

  ${StatsChart.fragments.statistic}
`;

const QUERIES = {
  invoices: makeQuery("invoices"),
  invoicesIncome: makeQuery("invoicesIncome"),
  soldItems: makeQuery("soldItems"),
  signatureMouthpieces: makeQuery("signatureMouthpieces"),
  discountCodesUsage: makeQuery("discountCodesUsage"),
  orderReturns: makeQuery("orderReturns"),
  orderFirstShipping: makeQuery("orderFirstShipping"),
  orderShipments: makeQuery("orderShipments"),
  ordersSoundShapings: makeQuery("ordersSoundShapings"),
  usersCreated: makeQuery("usersCreated"),
};

const START_OF_YEAR = new Date(new Date().getFullYear(), 0, 1, 1);

export default function StatsPage() {
  const [isB2B, setIsB2B] = React.useState(false);
  const [start, setStart] = React.useState(START_OF_YEAR);
  const [end, setEnd] = React.useState(new Date());
  const [step, setStep] = React.useState("MONTHS");

  const variables = { isB2B, step, range: { start, end } };

  return (
    <Container fixed sx={{ p: 2 }}>
      <Layout.Toolbar fixed>
        <Stack spacing={2} direction="row" alignItems="center">
          <Typography variant="h4" color="primary" sx={{ flexGrow: 1 }}>
            Statistiques
          </Typography>

          <RadioGroup
            row
            value={isB2B ? "B2B" : "B2C"}
            onChange={(e, value) => setIsB2B(value === "B2B")}
          >
            <FormControlLabel
              value="B2C"
              control={<Radio size="small" />}
              label="B2C"
            />
            <FormControlLabel
              value="B2B"
              control={<Radio size="small" />}
              label="B2B"
            />
          </RadioGroup>

          <TextField
            type="date"
            onChange={(e) => setStart(e.target.valueAsDate)}
            value={start?.toISOString().split("T")[0] ?? null}
            label="Début"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          <TextField
            type="date"
            value={end?.toISOString().split("T")[0] ?? null}
            onChange={(e) => setEnd(e.target.valueAsDate)}
            label="Fin"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          <TextField
            select
            value={step}
            onChange={(evt) => setStep(evt.target.value)}
            label="Granularité"
            sx={{ width: 140 }}
          >
            <MenuItem value="YEARS">Année</MenuItem>
            <MenuItem value="MONTHS">Mois</MenuItem>
            <MenuItem value="WEEKS">Semaine</MenuItem>
            <MenuItem value="DAYS">Jour</MenuItem>
          </TextField>
        </Stack>
      </Layout.Toolbar>

      <Stack spacing={2}>
        <StatsCard title="🎷 Ventes">
          <StatsChart query={QUERIES.invoices} variables={variables} />
          <Typography align="center">
            Nombre de factures émises par région.
          </Typography>
        </StatsCard>

        <StatsCard title="💰 Chiffre d’affaire">
          <StatsChart query={QUERIES.invoicesIncome} variables={variables} />
          <Typography align="center">
            Total cumulé des facture (HT) par région.
          </Typography>
        </StatsCard>

        <StatsCard title="🔝 Produits vendus">
          <StatsChart query={QUERIES.soldItems} variables={variables} />
          <Typography align="center">Compte de produits vendus.</Typography>
        </StatsCard>

        <StatsCard title="🖋️ Becs signature">
          <StatsChart
            query={QUERIES.signatureMouthpieces}
            variables={variables}
          />
          <Typography align="center">
            Compte de becs signature vendus.
          </Typography>
        </StatsCard>

        <StatsCard title="🎁 Codes de réduction">
          <StatsChart
            query={QUERIES.discountCodesUsage}
            variables={variables}
          />
          <Typography align="center">
            Utilisation des codes de réduction.
          </Typography>
        </StatsCard>

        <StatsCard title="🔁 Clients qui reviennent">
          <ReturningClients variables={variables} />
        </StatsCard>

        <StatsCard title="💢 Retours clients">
          <StatsChart query={QUERIES.orderReturns} variables={variables} />
        </StatsCard>

        <StatsCard title="🚚 Première livraison">
          <StatsChart
            query={QUERIES.orderFirstShipping}
            variables={variables}
          />
        </StatsCard>

        <StatsCard title="📦 Colis">
          <StatsChart query={QUERIES.orderShipments} variables={variables} />
        </StatsCard>

        <StatsCard title="🔧 Sound shaping">
          <StatsChart
            query={QUERIES.ordersSoundShapings}
            variables={variables}
          />

          <Typography align="center" gutterBottom>
            Nombre de sound shaping moyen par commande.
          </Typography>
          <Typography>
            <small>
              Seul les commandes terminées & réglées sur le site sont prises en
              compte.
              <br />
              Le regroupement par date se fait sur la date de création de la
              commande. <br />
              Le regroupement par type de sax se fait sur le type de bec utilisé
              lors du sound shaping.
            </small>
          </Typography>
        </StatsCard>

        <StatsCard title="👶 Nouveaux utilisateurs">
          <StatsChart query={QUERIES.usersCreated} variables={variables} />
          <Typography align="center">
            Nouvelle adresses mails collectées.
          </Typography>
        </StatsCard>
      </Stack>
    </Container>
  );
}

const RETURNING_CLIENTS_QUERY = gql`
  query (
    $step: StatisticsArg_Step
    $range: StatisticsArg_DateRange
    $isB2B: Boolean!
  ) {
    statistics {
      ordersReturningClient(step: $step, range: $range, isB2B: $isB2B)
    }
  }
`;

function ReturningClients({ query, variables, ...props }) {
  const { data, loading } = useQuery(RETURNING_CLIENTS_QUERY, { variables });

  if (loading) return <Layout.Loading />;
  if (!data?.statistics.ordersReturningClient) return null;

  return (
    <Typography>
      <span>
        Commandes payées (sur la période) par des utilisateurs ayant déjà
        commandé (payé) auparavant (hors période)&nbsp;:&nbsp;
      </span>
      <FormattedNumber
        value={data.statistics.ordersReturningClient}
        // eslint-disable-next-line react/style-prop-object
        style="percent"
      />
    </Typography>
  );
}
